import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FiArrowLeft, FiDownload, FiTrash2, FiRotateCcw, FiEye, FiUpload } from 'react-icons/fi';
import { useState } from 'react';
import { documentsAPI } from '../lib/api';
import useAuthStore from '../store/authStore';
import PermissionManager from '../components/PermissionManager';

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [restoreNotes, setRestoreNotes] = useState('');
  const profile = useAuthStore((state) => state.profile);
  const user = useAuthStore((state) => state.user);
  const isAdmin = profile?.role === 'admin';

  const { data, isLoading } = useQuery({
    queryKey: ['document', id],
    queryFn: () => documentsAPI.getById(id),
  });

  const { data: versionsData } = useQuery({
    queryKey: ['document-versions', id],
    queryFn: () => documentsAPI.getVersions(id),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: (docId) => documentsAPI.delete(docId),
    onSuccess: () => {
      queryClient.invalidateQueries(['documents']);
      navigate('/documents');
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (versionId) =>
      documentsAPI.restoreVersion(id, versionId, { change_notes: `Restored from version ${versionId}` }),
    onSuccess: () => {
      queryClient.invalidateQueries(['document', id]);
      queryClient.invalidateQueries(['document-versions', id]);
      setRestoreNotes('');
    },
  });

  const reprocessMutation = useMutation({
    mutationFn: (docId) => documentsAPI.reprocessAI(docId),
    onSuccess: () => {
      alert('AI processing started. The page will refresh in a few seconds...');
      setTimeout(() => {
        queryClient.invalidateQueries(['document', id]);
      }, 3000);
    },
  });

  const handleDownload = async () => {
    try {
      const response = await documentsAPI.download(id);

      // response.data should be a Blob when using responseType: 'blob'
      let blob = response.data;

      // Defensive: if it's not already a Blob, create one
      if (!(blob instanceof Blob)) {
        const contentType = response.headers['content-type'] || doc?.mime_type || 'application/octet-stream';
        blob = new Blob([blob], { type: contentType });
      }

      // Verify blob has content
      if (!blob || blob.size === 0) {
        throw new Error('Downloaded file is empty');
      }

      // Extract filename from Content-Disposition header or use document name
      let filename = doc?.name || 'download';
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();

      // Clean up
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download document: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleViewInBrowser = async () => {
    try {
      const response = await documentsAPI.download(id);

      // Handle Blob response
      let blob = response.data;
      if (!(blob instanceof Blob)) {
        blob = new Blob([blob], { type: doc?.mime_type || 'application/pdf' });
      }
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Clean up after a delay to ensure the window has time to load
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('View error:', error);
      alert('Failed to open document in browser');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">Loading document...</p>
      </div>
    );
  }

  const doc = data?.data?.document;

  if (!doc) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-600">Document not found</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/documents')}
            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            title="Back"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{doc.name}</h1>
            {doc.version && doc.version > 1 && (
              <span className="text-sm text-gray-600">Current Version: v{doc.version}</span>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleViewInBrowser}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="View in Browser"
          >
            <FiEye className="w-5 h-5" />
          </button>
          <button
            onClick={handleDownload}
            className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
            title="Download"
          >
            <FiDownload className="w-5 h-5" />
          </button>
          <button
            onClick={() => navigate('/search')}
            className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors"
            title="Upload from SharePoint"
          >
            <FiUpload className="w-5 h-5" />
          </button>
          {isAdmin && (
            <button
              onClick={() => {
                if (confirm('Are you sure you want to delete this document?')) {
                  deleteMutation.mutate(id);
                }
              }}
              className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete (Admin Only)"
            >
              <FiTrash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Document Info Card */}
          <div className="card">
            <h2 className="text-xl font-bold mb-4">Document Information</h2>
            <dl className="space-y-3">
              <div>
                <dt className="text-sm text-gray-600">File Size</dt>
                <dd className="text-gray-900 font-medium">{(doc?.file_size / 1024).toFixed(2)} KB</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600">MIME Type</dt>
                <dd className="text-gray-900 font-medium">{doc?.mime_type}</dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600">Uploaded</dt>
                <dd className="text-gray-900 font-medium">{new Date(doc?.created_at).toLocaleString()}</dd>
              </div>
              {doc?.description && (
                <div>
                  <dt className="text-sm text-gray-600">Description</dt>
                  <dd className="text-gray-900">{doc.description}</dd>
                </div>
              )}
              {doc?.tags?.length > 0 && (
                <div>
                  <dt className="text-sm text-gray-600">Tags</dt>
                  <dd className="flex flex-wrap gap-2 mt-2">
                    {doc.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 bg-primary-100 text-primary-700 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </dd>
                </div>
              )}
            </dl>
          </div>

          {/* Extracted Fields */}
          {(() => {
            const aiMetadata = doc?.document_ai_metadata?.[0] || doc?.document_ai_metadata;
            
            // Helper function to format complex values
            const formatValue = (value) => {
              if (value === null || value === undefined) return 'N/A';
              if (typeof value === 'object') {
                if (Array.isArray(value)) {
                  // Format arrays nicely
                  return value.map((item, idx) => (
                    <div key={idx} className="text-xs bg-gray-50 px-2 py-1 rounded mb-1">
                      {typeof item === 'object' ? JSON.stringify(item, null, 2) : String(item)}
                    </div>
                  ));
                }
                // Format objects as JSON
                return (
                  <pre className="text-xs bg-gray-50 px-2 py-1 rounded overflow-x-auto">
                    {JSON.stringify(value, null, 2)}
                  </pre>
                );
              }
              return String(value);
            };
            
            return aiMetadata?.extracted_fields && Object.keys(aiMetadata.extracted_fields).length > 0 && (
              <div className="card">
                <h2 className="text-xl font-bold mb-4">Extracted Fields</h2>
                <dl className="space-y-3 text-sm">
                  {Object.entries(aiMetadata.extracted_fields).map(([key, value]) => (
                    <div key={key} className="border-b border-gray-100 pb-2 last:border-0">
                      <dt className="text-gray-600 capitalize font-medium mb-1">{key.replace(/_/g, ' ')}</dt>
                      <dd className="text-gray-900">{formatValue(value)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })()}

          {/* AI Summary Card */}
          {doc?.document_content?.[0]?.summary && (
            <div className="card">
              <h2 className="text-xl font-bold mb-4">AI Summary</h2>
              <p className="text-gray-700 leading-relaxed">{doc.document_content[0].summary}</p>
              {doc.document_content[0].processing_status && (
                <p className="text-xs text-gray-500 mt-3">
                  Status: <span className="capitalize">{doc.document_content[0].processing_status}</span>
                </p>
              )}
            </div>
          )}

          {/* Extracted Text Card */}
          {doc?.document_content?.[0]?.extracted_text && (
            <div className="card">
              <h2 className="text-xl font-bold mb-4">Extracted Text</h2>
              <div className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 text-sm text-gray-700 whitespace-pre-wrap font-mono">
                {doc.document_content[0].extracted_text.substring(0, 2000)}
                {doc.document_content[0].extracted_text.length > 2000 && (
                  <p className="text-gray-500 mt-2">... (truncated, showing first 2000 characters)</p>
                )}
              </div>
            </div>
          )}

          {/* Version History Card */}
          {versionsData?.data?.versions && versionsData.data.versions.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-bold mb-4">Version History ({versionsData.data.versions.length})</h2>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {versionsData.data.versions.map((version) => (
                  <div
                    key={version.id}
                    className="p-3 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <p className="font-medium text-gray-900">Version {version.version_number}</p>
                          {version.version_number === doc.version && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded font-medium">
                              Current
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-600">
                          {new Date(version.created_at).toLocaleString()}
                        </p>
                        {version.change_notes && (
                          <p className="text-sm text-gray-700 mt-2">{version.change_notes}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          {(version.file_size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={async () => {
                            try {
                              const response = await documentsAPI.downloadVersion(id, version.id);
                              let blob = response.data;
                              if (!(blob instanceof Blob)) {
                                blob = new Blob([blob], { type: version.mime_type || doc.mime_type });
                              }

                              // Extract filename from Content-Disposition header or generate it
                              let filename = `${doc.name}_v${version.version_number}`;
                              const contentDisposition = response.headers['content-disposition'];
                              if (contentDisposition) {
                                const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
                                if (filenameMatch) {
                                  filename = decodeURIComponent(filenameMatch[1]);
                                }
                              } else {
                                // Generate filename with version before extension
                                const lastDotIndex = doc.name.lastIndexOf('.');
                                if (lastDotIndex > 0) {
                                  const nameWithoutExt = doc.name.substring(0, lastDotIndex);
                                  const ext = doc.name.substring(lastDotIndex);
                                  filename = `${nameWithoutExt}_v${version.version_number}${ext}`;
                                }
                              }

                              const url = window.URL.createObjectURL(blob);
                              const link = document.createElement('a');
                              link.href = url;
                              link.setAttribute('download', filename);
                              document.body.appendChild(link);
                              link.click();
                              link.remove();
                              window.URL.revokeObjectURL(url);
                            } catch (error) {
                              console.error('Download error:', error);
                              alert('Failed to download version');
                            }
                          }}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Download this version"
                        >
                          <FiDownload className="w-4 h-4" />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              const response = await documentsAPI.downloadVersion(id, version.id);
                              let blob = response.data;
                              if (!(blob instanceof Blob)) {
                                blob = new Blob([blob], { type: version.mime_type || doc.mime_type });
                              }
                              const url = window.URL.createObjectURL(blob);
                              window.open(url, '_blank');
                              setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                            } catch (error) {
                              console.error('View error:', error);
                              alert('Failed to view version');
                            }
                          }}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded transition-colors"
                          title="View this version"
                        >
                          <FiEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            if (
                              confirm(
                                `Restore this document to version ${version.version_number}? A new version will be created with the restored content.`
                              )
                            ) {
                              restoreMutation.mutate(version.id);
                            }
                          }}
                          disabled={restoreMutation.isPending}
                          className="p-2 text-primary-600 hover:bg-primary-100 rounded transition-colors disabled:opacity-50"
                          title="Restore this version"
                        >
                          <FiRotateCcw className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* AI Metadata */}
          {(doc?.document_ai_metadata?.[0] || doc?.document_ai_metadata) ? (
            <div className="card">
              <h2 className="text-lg font-bold mb-4">AI Summary</h2>
              
              {/* Get AI metadata - handle both array and object format */}
              {(() => {
                const aiMetadata = doc.document_ai_metadata?.[0] || doc.document_ai_metadata;
                const contentData = doc.document_content?.[0] || doc.document_content;
                
                return (
                  <>
                    {/* Display Summary */}
                    {contentData?.summary ? (
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <p className="text-gray-700 text-sm leading-relaxed">
                          {contentData.summary}
                        </p>
                      </div>
                    ) : (
                      <div className="mb-4 pb-4 border-b border-gray-200">
                        <p className="text-gray-500 text-sm italic">
                          Summary is being generated...
                        </p>
                      </div>
                    )}

                    <dl className="space-y-4">
                      <div>
                        <dt className="text-xs text-gray-600 uppercase tracking-wide">Document Type</dt>
                        <dd className="text-gray-900 font-semibold mt-1">{aiMetadata.document_type}</dd>
                      </div>
                      <div>
                        <dt className="text-xs text-gray-600 uppercase tracking-wide">Confidence</dt>
                        <dd className="flex items-center mt-1">
                          <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{
                                width: `${(aiMetadata.confidence_score * 100).toFixed(0)}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {(aiMetadata.confidence_score * 100).toFixed(0)}%
                          </span>
                        </dd>
                      </div>
                      {aiMetadata.suggested_tags?.length > 0 && (
                        <div>
                          <dt className="text-xs text-gray-600 uppercase tracking-wide">Suggested Tags</dt>
                          <dd className="flex flex-wrap gap-1 mt-2">
                            {aiMetadata.suggested_tags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded"
                              >
                                {tag}
                              </span>
                            ))}
                          </dd>
                        </div>
                      )}
                    </dl>
                  </>
                );
              })()}
            </div>
          ) : (
            <div className="card">
              <h2 className="text-lg font-bold mb-4">AI Summary</h2>
              <p className="text-gray-500 text-sm mb-4">AI summary pending or unavailable</p>
              <button
                onClick={() => {
                  if (confirm('Start AI processing for this document? This will extract text, generate a summary, and classify the document.')) {
                    reprocessMutation.mutate(id);
                  }
                }}
                disabled={reprocessMutation.isPending}
                className="btn-primary text-sm"
              >
                {reprocessMutation.isPending ? 'Processing...' : 'Process with AI'}
              </button>
            </div>
          )}

          {/* Permissions Card - Only visible to admin users */}
          {isAdmin && (
            <div className="card mt-6">
              <h2 className="text-xl font-bold mb-4">Document Permissions</h2>
              <PermissionManager
                resourceId={id}
                resourceType="document"
                currentUser={user}
                onPermissionsChange={() => {
                  queryClient.invalidateQueries(['document', id]);
                }}
                getRolePermissions={documentsAPI.getRolePermissions}
                setRolePermissions={documentsAPI.setRolePermissions}
                getUserPermissions={documentsAPI.getPermissions}
                grantUserPermission={documentsAPI.grantPermission}
                revokeUserPermission={documentsAPI.revokePermission}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentDetail;
